import React, { useEffect, useMemo, useState } from "react";
import { ThreeDots } from "react-loader-spinner";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import * as Yup from "yup";

import { getApi, postApi } from "../../utils/api";
import { showError, showSuccess } from "../../v2/utils/toast.ts";
import { API_REQUEST_ASSET, ASSET_TYPE_LIST } from "../../config/Endpoints";
import {
  Button,
  Select,
  TextArea,
} from "../../v2/components/ui/index.ts";

import styles from "./it-requests-form.module.css";

const AssetRequest = ({ title, description }) => {
  const [assetTypeList, setAssetTypeList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isListLoading, setIsListLoading] = useState(false);
  const accessToken = Cookies.get("accessToken");
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    selectedAssetId: "",
    reason: "",
  });
  const [errors, setErrors] = useState("");
  const validationSchema = Yup.object().shape({
    reason: Yup.string()
      .required("Reason is required.")
      .trim()
      .min(5, "Reason must be at least 5 characters long.")
      .max(200, "Reason cannot exceed 200 characters."),
  });

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));

    setErrors((prevFormData) => ({
      ...prevFormData,
      [name]: "",
    }));
  };

  useEffect(() => {
    fetchAssetTypeList();
  }, []);

  const fetchAssetTypeList = async () => {
    setIsListLoading(true);
    await getApi(ASSET_TYPE_LIST, accessToken)
      .then((res) => {
        if (res.status === 200) {
          setAssetTypeList(res.data.data);
          setIsListLoading(false);
        }
      })
      .catch((e) => {
        console.log(e);
        setIsListLoading(false);
      });
  };

  const handleSubmit = async (e) => {
    setIsLoading(true);
    e.preventDefault();

    try {
      let requestData = {
        assestTypeId: formData.selectedAssetId,
        reason: formData.reason,
      };

      await validationSchema.validate(formData, {
        abortEarly: false,
      });

      await postApi(API_REQUEST_ASSET, requestData, accessToken)
        .then((res) => {
          if (res.status === 200) {
            setIsLoading(false);
            showSuccess(res.data?.message ?? "Request submitted successfully.");
            navigate("/dashboard/assetRequest");
          } else {
            setIsLoading(false);
            showError(res.data?.message ?? "Request could not be submitted.");
          }
        })
        .catch((e) => {
          setIsLoading(false);
          showError(
            e?.response?.data?.message ??
              "Something went wrong please try again.",
          );
        });
    } catch (error) {
      setIsLoading(false);
      const validationErrors = {};
      error.inner.forEach((err) => {
        validationErrors[err.path] = err.message;
      });
      setErrors(validationErrors);
    }
  };
  const handleReset = (e) => {
    e.preventDefault();
    setFormData({
      selectedAssetId: "",
      reason: "",
    });

    setErrors({});
  };

  const assetTypeOptions = useMemo(
    () =>
      assetTypeList.map((item) => ({
        value: String(item.assestId),
        label: item.name,
      })),
    [assetTypeList]
  );

  const selectedAssetValue =
    formData.selectedAssetId !== "" && formData.selectedAssetId != null
      ? String(formData.selectedAssetId)
      : "";

  const handleAssetTypeChange = (nextValue) => {
    if (nextValue === "") {
      setFormData((prev) => ({ ...prev, selectedAssetId: "" }));
      return;
    }
    const matched = assetTypeList.find(
      (item) => String(item.assestId) === nextValue
    );
    setFormData((prev) => ({
      ...prev,
      selectedAssetId: matched ? matched.assestId : nextValue,
    }));
  };

  return (
    <form id="assign-asset" onSubmit={handleSubmit} className={styles.formRoot}>
      <div className="inputs">
        <div className="row">
          <div className="col-md-2"></div>
          <div className="col-md-7">
            <Select
              id="assetType"
              label="Asset Type"
              labelLayout="inline"
              required
              placeholder="Select Asset Type"
              options={assetTypeOptions}
              value={selectedAssetValue}
              onChange={handleAssetTypeChange}
              isClearable
              searchable
            />
          </div>
          <div className="col-md-3"></div>
        </div>

        <div className="row mt-1">
          <div className="col-md-2"></div>
          <div className="col-md-7">
            <TextArea
              id="reason"
              name="reason"
              label="Reason"
              labelLayout="inline"
              required
              rows={4}
              value={formData.reason}
              onChange={handleInputChange}
              error={errors.reason}
            />
          </div>
          <div className="col-md-3"></div>
        </div>
      </div>

      <br />

      <div className="action text-center d-flex justify-content-center gap-2">
        <Button
          type="button"
          variant="light-grey"
          onClick={handleReset}
        >
          Reset
        </Button>
        <Button type="submit" variant="accent" disabled={isLoading}>
          {isLoading ? (
            <span className={styles.submittingDots}>
              <ThreeDots
                height="20"
                width="44"
                radius="9"
                color="white"
                ariaLabel="three-dots-loading"
                visible
              />
            </span>
          ) : (
            "Submit"
          )}
        </Button>
      </div>
    </form>
  );
};

export default AssetRequest;
